import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RefreshCw, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getHistoryIcon,
  getSlaEscalationView,
  getSlaBreachTitle,
  formatHistoryDate,
  describeGroupChange,
  normalizeHistoryEntry,
  AUTOMATION_DATA_EVENT,
} from './historyUtils';

export interface HistoryEntry {
  id: string;
  eventType: string;
  title: string;
  description?: string | null;
  actorId?: string | null;
  actorName?: string | null;
  actorType?: string;
  ticketId?: string | null;
  customerId?: string | null;
  metadata: Record<string, unknown>;
  occurredAt: string;
  createdAt: string;
  // For customer history backward compat
  type?: string;
  category?: string;
}

interface HistoryTimelineProps {
  entries: HistoryEntry[];
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onRefresh?: () => void;
  onTicketClick?: (ticketId: string) => void;
  showFilters?: boolean;
  emptyMessage?: string;
}

const EVENT_TYPE_CATEGORIES: Record<string, string[]> = {
  'Status & Assignment': [
    'ticket.created', 'ticket.status_changed', 'ticket.assigned', 'ticket.unassigned',
    'ticket.secondary_assignee_added', 'ticket.secondary_assignee_removed',
    'ticket.resolved', 'ticket.reopened', 'ticket.closed', 'ticket.first_response',
  ],
  'Messages': [
    'ticket.message_sent', 'ticket.message_received', 'ticket.internal_note',
    'customer.message_sent', 'customer.message_received',
  ],
  'Pipeline & Fields': [
    'ticket.stage_changed', 'ticket.pipeline_changed', 'ticket.priority_changed',
    'ticket.category_changed', 'ticket.type_changed', 'ticket.field_changed',
    'ticket.custom_field_changed', 'ticket.custom_fields_changed',
    // Grouped multi-field edits land here so the filter surfaces them.
    'ticket.updated',
  ],
  'Notes & Line Items': [
    'ticket.note_added', 'ticket.note_updated', 'ticket.note_deleted',
    'ticket.note_pinned', 'ticket.note_unpinned',
    'ticket.line_items_added', 'ticket.line_item_updated', 'ticket.line_items_synced_from_email',
    // Granular per-field line-item change (nested in a group, and surfaced
    // standalone when only one field changed).
    'ticket.line_item_field_changed',
    'ticket.line_item_status_changed', 'ticket.line_item_deleted',
    'ticket.quote_generated',
  ],
  'Attachments': ['ticket.attachment_added', 'ticket.attachment_removed'],
  'Tags & Followers': [
    'ticket.tag_added', 'ticket.tag_removed', 'ticket.tags_changed',
    'ticket.follower_added', 'ticket.follower_removed',
  ],
  'SLA': ['ticket.sla_breach', 'ticket.sla_breached', 'ticket.sla_warning', 'ticket.sla_met', 'ticket.sla_escalated'],
  'Other': [
    // Automation's internal-field writes, collapsed to one neutral line.
    AUTOMATION_DATA_EVENT,
    'ticket.merged', 'customer.created', 'customer.updated', 'customer.ticket_created',
    'customer.ticket_resolved', 'customer.profile_updated', 'customer.merged',
    'customer.chat_started', 'customer.chat_ended', 'customer.login',
  ],
};

export function HistoryTimeline({
  entries,
  isLoading,
  hasMore,
  onLoadMore,
  onRefresh,
  onTicketClick,
  showFilters = true,
  emptyMessage = 'No history recorded yet',
}: HistoryTimelineProps) {
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  // Entries whose full detail (e.g. a description change) is expanded inline.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Close the filter dropdown on an outside click. Uses a listener (not a
  // full-screen overlay) so it never blocks scrolling the history list.
  const filterRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showFilterDropdown) return;
    const onDown = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showFilterDropdown]);

  // Strip automation-internal field writes before anything else looks at an
  // entry — filtering and rendering must agree on the normalized event type.
  const normalizedEntries = useMemo(
    () => entries.map(entry => ({ entry, view: normalizeHistoryEntry(entry) })),
    [entries],
  );

  const filteredEntries = selectedCategories.size === 0
    ? normalizedEntries
    : normalizedEntries.filter(({ view }) =>
        Array.from(selectedCategories).some(cat =>
          EVENT_TYPE_CATEGORIES[cat]?.includes(view.eventType),
        ),
      );

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  return (
    <div className="flex flex-col">
      {/* Header with filters */}
      {showFilters && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">History</h3>
            {selectedCategories.size > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                {selectedCategories.size} filter{selectedCategories.size > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <div className="relative" ref={filterRef}>
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                title="Filter history"
                className="p-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <Filter className="h-4 w-4" />
              </button>
              {showFilterDropdown && (
                <div className="absolute right-0 top-8 z-50 w-48 bg-white dark:bg-gray-800 rounded-md shadow-xl ring-1 ring-black/5 dark:ring-white/10 border border-gray-200 dark:border-gray-700 py-1">
                  {Object.keys(EVENT_TYPE_CATEGORIES).map(cat => (
                    <label
                      key={cat}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.has(cat)}
                        onChange={() => toggleCategory(cat)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-700"
                      />
                      <span className="text-gray-700 dark:text-gray-300">{cat}</span>
                    </label>
                  ))}
                  {selectedCategories.size > 0 && (
                    <button
                      onClick={() => setSelectedCategories(new Set())}
                      className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              )}
            </div>
            {onRefresh && (
              <button
                onClick={onRefresh}
                title="Refresh history"
                className="p-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="p-4">
        {isLoading && filteredEntries.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-5 w-5 text-gray-400 animate-spin dark:text-gray-500" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
            {emptyMessage}
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />

            <div className="space-y-4">
              {filteredEntries.map(({ entry, view }) => {
                const eventType = view.eventType;
                const { icon: Icon, color, bgColor } = getHistoryIcon(eventType);
                // SLA breach rows carry the breach instant in metadata; the time
                // is rendered here in the viewer's zone, not baked into the stored
                // title (BUG-019 / #18927).
                const title = getSlaBreachTitle(
                  eventType,
                  view.title || entry.description || eventType,
                  entry.metadata,
                );
                const ticketId = entry.ticketId;
                const isAutomationLine = eventType === AUTOMATION_DATA_EVENT;

                // Description edits keep the full before/after text in metadata
                // (title only says "Description updated"). Show a short "…" preview
                // that expands to the full old/new text on click.
                const meta = (entry.metadata || {}) as Record<string, unknown>;
                const toVal = typeof meta.to === 'string' ? meta.to : '';
                const fromVal = typeof meta.from === 'string' ? meta.from : '';
                const isDescChange =
                  eventType === 'ticket.field_changed' &&
                  meta.field === 'description' &&
                  Boolean(toVal || fromVal);
                const isExpanded = expandedIds.has(entry.id);
                const preview = (toVal || fromVal).replace(/\s+/g, ' ').trim().slice(0, 60);

                // Grouped multi-field edit: the backend collapses 2+ field
                // changes from one save into a single summary entry
                // ("ticket.updated", or "ticket.line_item_updated" for one line
                // item) carrying each granular change in metadata.changes.
                // Render the summary line and spread the individual changes —
                // each with its own from → to values — on click.
                // (normalizeHistoryEntry only populates `changes` for group
                // event types, and has already stripped automation-internal
                // field writes, so its length is the whole gate here.)
                const groupChanges = view.changes;
                const isGroup = groupChanges.length > 0;

                // SLA escalation carries a structured audit block (who it came
                // from, who it went to, why, elapsed business time, whether
                // anyone was reached). Rendered as rows rather than through the
                // one-line truncated `description`, which would cut it off.
                // Returns null for every other event type.
                const slaEscalation = getSlaEscalationView(eventType, entry.metadata);

                return (
                  <div key={entry.id} className="relative flex items-start gap-3 pl-1">
                    {/* Icon */}
                    <div className={`relative z-10 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${bgColor}`}>
                      <Icon className={`h-3.5 w-3.5 ${color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900 dark:text-gray-100">{title}</p>
                          {/* The automation line is deliberately value-free — never
                              surface the raw internal payload underneath it. */}
                          {!isAutomationLine && !slaEscalation && entry.description && entry.description !== title && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                              {entry.description}
                            </p>
                          )}

                          {slaEscalation && (
                            <div className="mt-1.5">
                              <span
                                className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                  slaEscalation.reason === 'approaching_breach'
                                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                }`}
                              >
                                {slaEscalation.reasonLabel}
                              </span>
                              {slaEscalation.details.length > 0 && (
                                <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                                  {slaEscalation.details.map((detail) => (
                                    <Fragment key={detail.label}>
                                      <dt className="text-xs text-gray-400 dark:text-gray-500">
                                        {detail.label}
                                      </dt>
                                      <dd className="text-xs text-gray-600 dark:text-gray-300 break-words">
                                        {detail.value}
                                      </dd>
                                    </Fragment>
                                  ))}
                                </dl>
                              )}
                            </div>
                          )}

                          {/* Expandable description diff */}
                          {isDescChange && (
                            <div className="mt-1">
                              <button
                                type="button"
                                onClick={() => toggleExpand(entry.id)}
                                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-3 w-3 flex-shrink-0" />
                                ) : (
                                  <ChevronDown className="h-3 w-3 flex-shrink-0" />
                                )}
                                {isExpanded ? (
                                  <span>Hide details</span>
                                ) : (
                                  <span className="italic">
                                    “{preview}{(toVal || fromVal).length > 60 ? '…' : ''}”
                                  </span>
                                )}
                              </button>
                              {isExpanded && (
                                <div className="mt-1.5 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/50">
                                  {fromVal && (
                                    <div>
                                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                        Before
                                      </p>
                                      <p className="whitespace-pre-wrap break-words text-xs text-gray-600 dark:text-gray-400">
                                        {fromVal}
                                      </p>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                      After
                                    </p>
                                    <p className="whitespace-pre-wrap break-words text-xs text-gray-800 dark:text-gray-200">
                                      {toVal || <span className="italic text-gray-400">(empty)</span>}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {/* Grouped multi-field edit — collapsed summary that
                              spreads into one row per change on click. */}
                          {isGroup && (
                            <div className="mt-1">
                              <button
                                type="button"
                                onClick={() => toggleExpand(entry.id)}
                                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-3 w-3 flex-shrink-0" />
                                ) : (
                                  <ChevronDown className="h-3 w-3 flex-shrink-0" />
                                )}
                                <span>
                                  {isExpanded
                                    ? 'Hide changes'
                                    : `Show ${groupChanges.length} change${groupChanges.length === 1 ? '' : 's'}`}
                                </span>
                              </button>
                              {isExpanded && (
                                <ul className="mt-1.5 space-y-1 rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/50">
                                  {groupChanges.map((c, i) => {
                                    const { icon: CIcon, color: cColor } = getHistoryIcon(
                                      c.eventType || '',
                                    );
                                    const { label, detail } = describeGroupChange(c);
                                    return (
                                      <li
                                        key={i}
                                        className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300"
                                      >
                                        <CIcon className={`h-3 w-3 mt-0.5 flex-shrink-0 ${cColor}`} />
                                        <span>
                                          <span className="font-medium">{label}</span>
                                          {detail ? <span className="text-gray-500 dark:text-gray-400">: {detail}</span> : null}
                                        </span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            {entry.actorName && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                by {entry.actorName}
                              </span>
                            )}
                            {ticketId && onTicketClick && (
                              <button
                                onClick={() => onTicketClick(ticketId)}
                                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                View ticket
                              </button>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                          {formatHistoryDate(entry.occurredAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Load more */}
        {hasMore && onLoadMore && (
          <div className="mt-4 text-center">
            <button
              onClick={onLoadMore}
              disabled={isLoading}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium text-blue-600 hover:bg-gray-100 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <ChevronDown className="h-4 w-4" />
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
